import { useEffect, useMemo, useRef } from 'react'

/**
 * Runs `callback` after `secondsToTimeout` seconds without a click, touch, or
 * mouse movement, restarting the clock on each. The kiosk's idle logout.
 *
 * A null or non-positive timeout means this consumer doesn't own idle logout
 * right now, so nothing is scheduled at all.
 */
export default function useKioskIdle(secondsToTimeout, callback) {
  const savedCallback = useRef(callback)
  const savedTimer = useRef()
  const watchedActions = useMemo(() => ['click', 'touchstart', 'mousemove'], [])

  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  // Set up the interval.
  useEffect(() => {
    // A null/non-positive timeout means this consumer isn't the one that
    // should own idle-triggered logout right now - skip entirely. Otherwise
    // `secondsToTimeout * 1000` below would coerce `null` to `0`, and the
    // listeners registered further down would then fire the callback almost
    // immediately on the next click/touch/mousemove instead of never.
    if (secondsToTimeout === null || secondsToTimeout <= 0) return undefined

    const tick = () => {
      savedCallback.current()
    }

    const createNewTimer = () => {
      const id = setTimeout(tick, secondsToTimeout * 1000)
      return id
    }

    const resetIdleTimeout = () => {
      clearTimeout(savedTimer.current)
      savedTimer.current = createNewTimer()
    }

    watchedActions.forEach((evt) => document.addEventListener(evt, resetIdleTimeout, false))
    savedTimer.current = createNewTimer()
    return () => {
      watchedActions.forEach((evt) => document.removeEventListener(evt, resetIdleTimeout, false))
      clearTimeout(savedTimer.current)
    }
  }, [secondsToTimeout, watchedActions])
}
